import { Button, HStack, Textarea } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaEnvelope } from 'react-icons/fa'
import { LuCheck } from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { Heading } from '@/components/atoms/Heading'
import { useColorModeValue } from '@/components/ui/color-mode'
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog'

interface RequestToJoinButtonProps {
  circleId: string
  /** Community name, used to prefill the request message. */
  circleName: string
  isPending: boolean
  isRequesting: boolean
  /** Sends the request. Resolving to false keeps the dialog open with the typed message. */
  onRequest: (circleId: string, message: string) => Promise<boolean> | void
  /** Stretch to the card width (grid usage). */
  fullWidth?: boolean
}

/**
 * "Request to join" CTA with a sent/pending state.
 *
 * Opens the same message dialog as the community page rather than firing the mutation on the
 * spot: a membership request without a word of introduction is much more likely to be turned
 * down, and the two entry points must not behave differently.
 */
export const RequestToJoinButton = ({
  circleId,
  circleName,
  isPending,
  isRequesting,
  onRequest,
  fullWidth = true,
}: RequestToJoinButtonProps) => {
  const { t } = useTranslation()
  const auth = useAuth()
  const dialogBg = useColorModeValue('blue.50', 'blue.900')

  const defaultMessage = () =>
    t('communities.show.actions.askToJoin.modal.placeholder', {
      name: auth.user?.firstname,
      community: { name: circleName },
      context: auth.user?.gender,
    })

  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState(defaultMessage)

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Rebuilt on every open: the user may have edited it, then cancelled.
      setMessage(defaultMessage())
    }
    setOpen(nextOpen)
  }

  const handleSend = async () => {
    const sent = await onRequest(circleId, message)
    if (sent !== false) {
      setOpen(false)
    }
  }

  return (
    <>
      <Button
        w={fullWidth ? 'full' : undefined}
        colorPalette="brand"
        variant={isPending ? 'subtle' : 'solid'}
        disabled={isPending}
        loading={isRequesting}
        onClick={() => handleOpenChange(true)}
      >
        {isPending ? (
          <>
            <LuCheck />
            {t('register.wizard.profiles.member.join.requestedShort')}
          </>
        ) : (
          t('register.wizard.profiles.member.join.cta')
        )}
      </Button>

      <DialogRoot
        lazyMount
        open={open}
        onOpenChange={(e: { open: boolean }) => handleOpenChange(e.open)}
        placement="center"
        closeOnEscape
      >
        {/* Above the onboarding dialog, which hosts one of the two call sites. */}
        <DialogContent bg={dialogBg} rounded="2xl" zIndex={1600}>
          <DialogHeader>
            <Heading size="lg">
              {t('communities.show.actions.askToJoin.modal.title')}
            </Heading>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Textarea
              rows={10}
              aria-label={t('communities.show.actions.askToJoin.modal.title')}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <HStack gap={3}>
              <DialogActionTrigger asChild>
                <Button variant="outline">{t('global:cancel')}</Button>
              </DialogActionTrigger>
              <Button
                colorPalette="brand"
                loading={isRequesting}
                onClick={() => void handleSend()}
              >
                <FaEnvelope />
                {t('global:send')}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  )
}
